import React from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Container,
    Paper,
    Typography,
    useTheme,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import { ErrorSeverity, getErrorMessage } from '@services/errorHandling';

export interface ErrorDisplayProps {
    /** Error object or message */
    error: unknown;
    /** Error severity (defaults to ERROR) */
    severity?: ErrorSeverity;
    /** Custom title (optional) */
    title?: string;
    /** Custom message (optional, overrides extracted message) */
    message?: string;
    /** Show retry button */
    onRetry?: () => void;
    /** Show home button */
    onHome?: () => void;
    /** Additional context to display (dev only) */
    context?: Record<string, unknown>;
    /** Variant: 'inline' (alert style) or 'page' (full page error) */
    variant?: 'inline' | 'page';
}

/**
 * Displays errors to users with appropriate styling and actions
 *
 * Usage:
 * ```tsx
 * // Inline alert
 * <ErrorDisplay error={error} onRetry={refetch} />
 *
 * // Full page error
 * <ErrorDisplay error={error} variant="page" onHome={() => navigate('/')} />
 * ```
 */
export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
    error,
    severity = ErrorSeverity.ERROR,
    title,
    message,
    onRetry,
    onHome,
    context,
    variant = 'inline',
}) => {
    const theme = useTheme();
    const isDevelopment = process.env.NODE_ENV === 'development';

    // Extract user-friendly message
    const errorMessage = message || getErrorMessage(error);

    // Determine MUI alert severity from our ErrorSeverity
    const alertSeverity = severity === ErrorSeverity.CRITICAL || severity === ErrorSeverity.ERROR
        ? 'error'
        : severity === ErrorSeverity.WARNING
        ? 'warning'
        : 'info';

    // Get icon based on severity
    const getIcon = () => {
        switch (severity) {
            case ErrorSeverity.CRITICAL:
            case ErrorSeverity.ERROR:
                return <ErrorIcon />;
            case ErrorSeverity.WARNING:
                return <WarningIcon />;
            default:
                return <InfoIcon />;
        }
    };

    // Default titles based on severity
    const defaultTitle = title || (
        severity === ErrorSeverity.CRITICAL ? 'Critical Error' :
        severity === ErrorSeverity.ERROR ? 'Error' :
        severity === ErrorSeverity.WARNING ? 'Warning' :
        'Information'
    );

    if (variant === 'page') {
        return (
            <Container maxWidth="md" sx={{ mt: 8 }}>
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: theme.palette.mode === 'dark'
                            ? theme.palette.grey[900]
                            : theme.palette.grey[50],
                    }}
                >
                    <Box sx={{ mb: 3, color: theme.palette[alertSeverity].main }}>
                        {React.cloneElement(getIcon(), { sx: { fontSize: 80 } })}
                    </Box>

                    <Typography variant="h4" component="h1" gutterBottom>
                        {defaultTitle}
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        {errorMessage}
                    </Typography>

                    {isDevelopment && context && Object.keys(context).length > 0 && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                backgroundColor: theme.palette.background.default,
                                borderRadius: 1,
                                textAlign: 'left',
                            }}
                        >
                            <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                                <strong>Debug Context:</strong>
                                <pre>{JSON.stringify(context, null, 2)}</pre>
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
                        {onRetry && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<RefreshIcon />}
                                onClick={onRetry}
                            >
                                Retry
                            </Button>
                        )}
                        {onHome && (
                            <Button
                                variant="outlined"
                                startIcon={<HomeIcon />}
                                onClick={onHome}
                            >
                                Go Home
                            </Button>
                        )}
                    </Box>
                </Paper>
            </Container>
        );
    }

    // Inline variant
    return (
        <Alert
            severity={alertSeverity}
            icon={getIcon()}
            action={
                onRetry && (
                    <Button
                        color="inherit"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={onRetry}
                    >
                        Retry
                    </Button>
                )
            }
            sx={{ mb: 2 }}
        >
            <AlertTitle>{defaultTitle}</AlertTitle>
            {errorMessage}
            {isDevelopment && context && Object.keys(context).length > 0 && (
                <Box sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    <details>
                        <summary>Debug Info</summary>
                        <pre>{JSON.stringify(context, null, 2)}</pre>
                    </details>
                </Box>
            )}
        </Alert>
    );
};

/**
 * Simple inline error alert
 */
export const ErrorAlert: React.FC<{ error: unknown; onRetry?: () => void }> = ({
    error,
    onRetry,
}) => {
    return <ErrorDisplay error={error} variant="inline" onRetry={onRetry} />;
};

/**
 * Full page error screen
 */
export const ErrorPage: React.FC<{
    error: unknown;
    onRetry?: () => void;
    onHome?: () => void;
}> = ({ error, onRetry, onHome }) => {
    return <ErrorDisplay error={error} variant="page" onRetry={onRetry} onHome={onHome} />;
};
